import React, { useEffect, useRef, useState } from 'react';

/**
 * Site settings panel (modal), mounted once from Nav so it exists on every
 * page. Opened from the desktop top utility row and from the top of the
 * mobile drawer via the aw-open-settings window event.
 *
 * Settings persist per-browser in localStorage:
 *   aw_theme              theme id ('classic' | 'refined' | 'apple' | ...)
 *   aw_accent             optional custom accent hex, overlays any theme
 *   aw_quickmenu_hidden   '1' hides the mobile quick menu (pill strips)
 *
 * Theme is applied as data-aw-theme on <html>; accent as the --aw-accent
 * custom property + a data-aw-accent marker attribute. Both are applied at
 * boot in src/index.js so there's no flash of the wrong styling.
 */

export const THEME_KEY = 'aw_theme';
export const SCHEME_KEY = 'aw_scheme'; // 'auto' | 'light' | 'dark'
export const ACCENT_KEY = 'aw_accent';
export const QUICKMENU_KEY = 'aw_quickmenu_hidden';

// Classic Refined (in the classic green) is the site default; Classic —
// the untouched original — sits right below it. Every theme has a dark
// variant — dark is an appearance setting (below), not a theme.
export const DEFAULT_THEME = 'refined';
export const THEMES = [
  { id: 'refined', label: 'Classic Refined', swatches: ['#1aba1a', '#f2f5f2', '#ffffff'] },
  { id: 'classic', label: 'Classic', swatches: ['#1aba1a', '#c6c6c6', '#ffffff'] },
  { id: 'aurora', label: 'Aurora Glass', swatches: ['#0d9488', '#e3f0fb', '#f0e9fb'] },
  { id: 'apple', label: 'Minimal', swatches: ['#1d1d1f', '#f5f5f7', '#fbfbfd'] },
  { id: 'fresh', label: 'Fresh Minimal', swatches: ['#16a34a', '#fafbfa', '#ffffff'] },
];

export const SCHEMES = [
  { id: 'light', label: 'Light' },
  { id: 'auto', label: 'Auto' },
  { id: 'dark', label: 'Dark' },
];

const darkMedia =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

export const resolveDark = (scheme) =>
  scheme === 'dark' || (scheme !== 'light' && Boolean(darkMedia && darkMedia.matches));

export const applyScheme = (scheme) => {
  document.documentElement.toggleAttribute('data-aw-dark', resolveDark(scheme));
  try { localStorage.setItem(SCHEME_KEY, scheme); } catch (e) { /* private mode */ }
};

// Preset accents the visitor can apply on top of any theme.
export const ACCENTS = [
  { name: 'Green', hex: '#1aba1a' },
  { name: 'Teal', hex: '#0d9488' },
  { name: 'Blue', hex: '#0066cc' },
  { name: 'Indigo', hex: '#4f46e5' },
  { name: 'Violet', hex: '#7c3aed' },
  { name: 'Rose', hex: '#e11d48' },
  { name: 'Amber', hex: '#d97706' },
  { name: 'Slate', hex: '#475569' },
];

export const openSettings = () => {
  window.dispatchEvent(new Event('aw-open-settings'));
};

export const applyTheme = (id) => {
  document.documentElement.setAttribute('data-aw-theme', id);
  try { localStorage.setItem(THEME_KEY, id); } catch (e) { /* private mode */ }
};

export const applyAccent = (hex) => {
  const root = document.documentElement;
  if (hex) {
    root.style.setProperty('--aw-accent', hex);
    root.setAttribute('data-aw-accent', '');
    try { localStorage.setItem(ACCENT_KEY, hex); } catch (e) { /* private mode */ }
  } else {
    root.style.removeProperty('--aw-accent');
    root.removeAttribute('data-aw-accent');
    try { localStorage.removeItem(ACCENT_KEY); } catch (e) { /* private mode */ }
  }
};

const readTheme = () => {
  try {
    const t = localStorage.getItem(THEME_KEY) || DEFAULT_THEME;
    return t === 'night' ? 'classic' : t; // retired theme, migrated at boot
  } catch (e) { return DEFAULT_THEME; }
};
const readScheme = () => {
  try { return localStorage.getItem(SCHEME_KEY) || 'auto'; } catch (e) { return 'auto'; }
};
const readAccent = () => {
  try { return localStorage.getItem(ACCENT_KEY) || null; } catch (e) { return null; }
};
const readQuickHidden = () => {
  try { return localStorage.getItem(QUICKMENU_KEY) === '1'; } catch (e) { return false; }
};

const SettingsPanel = () => {
  // Dev aid: ?_settings=open renders the panel open on first paint.
  const [open, setOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('_settings') === 'open';
    } catch (e) {
      return false;
    }
  });
  const [theme, setTheme] = useState(readTheme);
  const [scheme, setScheme] = useState(readScheme);
  const [accent, setAccent] = useState(readAccent);
  const [quickHidden, setQuickHidden] = useState(readQuickHidden);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      setTheme(readTheme());
      setScheme(readScheme());
      setAccent(readAccent());
      setQuickHidden(readQuickHidden());
      setOpen(true);
    };
    window.addEventListener('aw-open-settings', onOpen);
    return () => window.removeEventListener('aw-open-settings', onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    if (closeBtnRef.current) closeBtnRef.current.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chooseTheme = (id) => {
    setTheme(id);
    applyTheme(id);
  };

  const chooseScheme = (id) => {
    setScheme(id);
    applyScheme(id);
  };

  const chooseAccent = (hex) => {
    setAccent(hex);
    applyAccent(hex);
  };

  const resetToDefaults = () => {
    chooseTheme(DEFAULT_THEME);
    chooseAccent(null);
  };

  const toggleQuickMenu = () => {
    const next = !quickHidden;
    setQuickHidden(next);
    try {
      if (next) localStorage.setItem(QUICKMENU_KEY, '1');
      else localStorage.removeItem(QUICKMENU_KEY);
    } catch (e) { /* private mode */ }
    window.dispatchEvent(new Event('aw-settings-changed'));
  };

  if (!open) return null;

  return (
    <div
      className="aw-settings-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="aw-settings-card" role="dialog" aria-modal="true" aria-label="Site settings">
        <div className="aw-settings-head">
          <span>Site settings</span>
          <button
            ref={closeBtnRef}
            type="button"
            className="aw-settings-close"
            aria-label="Close settings"
            onClick={() => setOpen(false)}
          >
            &times;
          </button>
        </div>

        <div className="aw-settings-sect">Appearance</div>
        <div className="aw-scheme-row" role="radiogroup" aria-label="Appearance">
          {SCHEMES.map((s) => (
            <button
              key={s.id}
              type="button"
              className={'aw-scheme-btn' + (scheme === s.id ? ' selected' : '')}
              onClick={() => chooseScheme(s.id)}
              aria-pressed={scheme === s.id}
            >
              {s.label}
            </button>
          ))}
        </div>
        <p className="aw-scheme-hint">Auto follows your device&rsquo;s light/dark setting.</p>

        <div className="aw-settings-sect">Theme</div>
        <div className="aw-settings-themes">
          {THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'aw-theme-option' + (theme === t.id ? ' selected' : '')}
              onClick={() => chooseTheme(t.id)}
              aria-pressed={theme === t.id}
            >
              <span className="aw-theme-swatches" aria-hidden="true">
                {t.swatches.map((c, i) => (
                  <span key={i} style={{ backgroundColor: c }} />
                ))}
              </span>
              <span className="aw-theme-text">
                <span className="aw-theme-label">{t.label}</span>
              </span>
              {theme === t.id && <span className="aw-theme-check" aria-hidden="true">&#10003;</span>}
            </button>
          ))}
        </div>

        <div className="aw-settings-sect">Accent colour</div>
        <div className="aw-accent-row">
          {ACCENTS.map((a) => (
            <button
              key={a.hex}
              type="button"
              className={'aw-accent-dot' + (accent && accent.toLowerCase() === a.hex.toLowerCase() ? ' selected' : '')}
              style={{ backgroundColor: a.hex }}
              onClick={() => chooseAccent(a.hex)}
              aria-label={'Accent ' + a.name}
              aria-pressed={accent && accent.toLowerCase() === a.hex.toLowerCase()}
            />
          ))}
          <label className="aw-accent-custom" title="Pick any colour">
            <input
              type="color"
              value={accent || '#1aba1a'}
              onChange={(e) => chooseAccent(e.target.value)}
              aria-label="Custom accent colour"
            />
            <span aria-hidden="true">+</span>
          </label>
        </div>
        <button
          type="button"
          className="aw-accent-reset"
          onClick={() => chooseAccent(null)}
          disabled={!accent}
        >
          Use theme&rsquo;s own colour
        </button>

        <div className="aw-settings-sect">Phone &amp; tablet</div>
        <label className="aw-settings-toggle">
          <input type="checkbox" checked={!quickHidden} onChange={toggleQuickMenu} />
          <span className="aw-settings-toggle-text">
            <span className="aw-theme-label">Show quick menu</span>
            <span className="aw-theme-desc">The rows of buttons under the search box on small screens</span>
          </span>
        </label>

        <button
          type="button"
          className="aw-settings-reset"
          onClick={resetToDefaults}
          disabled={theme === DEFAULT_THEME && !accent}
        >
          Reset theme &amp; colour to default
        </button>

        <p className="aw-settings-foot">Settings are saved on this device only.</p>
      </div>
    </div>
  );
};

export default SettingsPanel;
